/**
 * Small DOM effects for the 2D layer — all CSS/WAAPI, nothing per frame, and all of them skip themselves when the
 * player asked for less motion (the OS setting or the in-game "Reduced motion" toggle on the root).
 */
export function motionOK(root: HTMLElement): boolean {
  if (root.classList.contains("reduced-motion")) return false;
  return !(typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
}

/** A quick squash-and-stretch pop on an element (score digits, badges, counters). */
export function bump(el: Element | null | undefined, root: HTMLElement, strength = 1): void {
  if (!el || !motionOK(root) || typeof (el as HTMLElement).animate !== "function") return;
  const s = 1 + 0.22 * strength;
  (el as HTMLElement).animate(
    [{ transform: "translateY(0) scale(1)" }, { transform: `translateY(${-14 * strength}%) scale(${s})`, offset: 0.35 }, { transform: "translateY(0) scale(1)" }],
    { duration: 260 + 80 * strength, easing: "cubic-bezier(.3,1.5,.5,1)" }
  );
}

/** Confetti: N little pieces that fly out of the centre of their parent (CSS keyframes; the parent is position:relative). */
export function confettiHtml(count = 18): string {
  const pieces = Array.from({ length: count }, (_, i) => {
    const angle = Math.round((360 / count) * i + (i % 2 ? 9 : -6));
    const dist = 70 + ((i * 37) % 60);
    const delay = (i % 4) * 25;
    return `<i style="--a:${angle}deg;--d:${dist}px;--t:${delay}ms"></i>`;
  }).join("");
  return `<span class="confetti" aria-hidden="true">${pieces}</span>`;
}

/**
 * The bonus that pops in the middle of the screen and flies into a HUD element (the multiplier badge): pop, hold,
 * fly, land. `target` is looked up when the flight starts, so the HUD may be rebuilt meanwhile. Resolves on landing.
 */
export function flyBonus(shell: HTMLElement, root: HTMLElement, label: string, target: () => Element | null, kind = "mult"): Promise<void> {
  const el = document.createElement("div");
  el.className = `fly-bonus ${kind}`;
  el.setAttribute("aria-hidden", "true");
  el.textContent = label;
  shell.append(el);
  if (!motionOK(root) || typeof el.animate !== "function") {
    return new Promise((resolve) => window.setTimeout(() => { el.remove(); resolve(); }, 900));
  }
  const pop = el.animate(
    [{ transform: "translate(-50%, -50%) scale(.2) rotate(-12deg)", opacity: 0 }, { transform: "translate(-50%, -50%) scale(1.25) rotate(4deg)", opacity: 1, offset: 0.6 }, { transform: "translate(-50%, -50%) scale(1) rotate(-3deg)", opacity: 1 }],
    { duration: 420, easing: "cubic-bezier(.2,1.4,.4,1)", fill: "forwards" }
  );
  return pop.finished.then(() => new Promise<void>((resolve) => window.setTimeout(resolve, 380))).then(() => {
    const goal = target()?.getBoundingClientRect();
    const from = el.getBoundingClientRect();
    if (!goal) { el.remove(); return; }
    const dx = goal.left + goal.width / 2 - (from.left + from.width / 2);
    const dy = goal.top + goal.height / 2 - (from.top + from.height / 2);
    const fly = el.animate(
      [{ transform: "translate(-50%, -50%) scale(1) rotate(-3deg)", opacity: 1 }, { transform: `translate(calc(-50% + ${dx * 0.55}px), calc(-50% + ${dy * 0.35 - 40}px)) scale(.8) rotate(8deg)`, opacity: 1, offset: 0.45 }, { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.25) rotate(0deg)`, opacity: 0.9 }],
      { duration: 520, easing: "cubic-bezier(.5,0,.75,.4)", fill: "forwards" }
    );
    return fly.finished.then(() => { el.remove(); });
  }).catch(() => { el.remove(); });
}

/** A full-width banner that sweeps across with speed lines (HEADSTART). Removes itself. */
export function sweepBanner(shell: HTMLElement, root: HTMLElement, text: string, iconHtml: string, kind = "headstart"): void {
  const el = document.createElement("div");
  el.className = `sweep-banner ${kind}${motionOK(root) ? "" : " still"}`;
  el.setAttribute("aria-hidden", "true");
  el.innerHTML = `<div class="sweep-band"><span class="sweep-lines"></span>${iconHtml}<strong>${text}</strong>${iconHtml}</div>`;
  shell.append(el);
  window.setTimeout(() => el.remove(), 1400);
}
